'use client'

import { ExtendedEvent } from '../types'
import {
  getGameName,
  getGameImageUrl,
  formatTimeRange,
  calculateAvailableSlots,
} from '../utils/event-processor'
import AccessibleEvent from './AccessibleEvent'

interface MobileViewEventProps {
  event: ExtendedEvent
  isCompact?: boolean
  onClick?: () => void
  onKeyboardNavigation?: (direction: 'up' | 'down' | 'left' | 'right') => void
}

/**
 * Component for rendering events optimized for mobile devices
 * Provides a more compact and touch-friendly interface
 */
export function MobileViewEvent({
  event,
  isCompact = false,
  onClick,
  onKeyboardNavigation,
}: MobileViewEventProps) {
  const { extendedProps } = event
  const gameName = getGameName(event)
  const gameImageUrl = getGameImageUrl(event)

  // Format the time range
  const startTime = event.start
    ? typeof event.start === 'string'
      ? event.start
      : event.start instanceof Date
      ? event.start.toISOString()
      : null
    : null

  const endTime = event.end
    ? typeof event.end === 'string'
      ? event.end
      : event.end instanceof Date
      ? event.end.toISOString()
      : null
    : null

  const timeRange = formatTimeRange(startTime, endTime)

  // Calculate player slots
  const maxPlayers = extendedProps?.maxPlayers || 0
  const currentPlayers = extendedProps?.participantCount || 0
  const availableSlots = calculateAvailableSlots(maxPlayers, currentPlayers)

  // Super compact view for very small screens
  const renderContent = () => {
    if (isCompact) {
      return (
        <div className="fc-event-custom-content flex items-center p-1">
          <div className="flex flex-col flex-1">
            <div className="fc-event-title font-medium text-xs truncate">{event.title}</div>
            <div className="fc-event-time text-xs opacity-80">{timeRange}</div>
          </div>
        </div>
      )
    }

    // Standard mobile view
    return (
      <div className="fc-event-custom-content flex touch-manipulation">
        {/* Only show image if not in month view to save space */}
        {gameImageUrl && !isCompact && (
          <div className="fc-event-thumbnail-container mr-2 flex-shrink-0">
            <img
              src={gameImageUrl}
              alt=""
              className="fc-event-thumbnail w-8 h-8 object-cover rounded-md"
              onError={e => {
                e.currentTarget.onerror = null
                e.currentTarget.src = '/placeholder-game.png'
              }}
              loading="lazy"
              aria-hidden="true"
            />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="fc-event-title font-medium text-xs truncate">{event.title}</div>
          {gameName && !isCompact && (
            <div className="fc-event-game-name text-xs opacity-80 truncate">{gameName}</div>
          )}
          <div className="fc-event-info text-xs">
            <span className="fc-event-time opacity-80">{timeRange}</span>
          </div>
          {maxPlayers > 0 && !isCompact && (
            <div className="fc-event-slots text-xs mt-1">
              <span
                className={`inline-flex px-1 py-0 text-xs rounded-full ${
                  currentPlayers >= maxPlayers
                    ? 'bg-red-900 text-red-200'
                    : currentPlayers >= maxPlayers * 0.8
                    ? 'bg-yellow-700 text-yellow-100'
                    : 'bg-green-800 text-green-100'
                }`}
              >
                {availableSlots} left
              </span>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <AccessibleEvent event={event} onClick={onClick} onKeyboardNavigation={onKeyboardNavigation}>
      {renderContent()}
    </AccessibleEvent>
  )
}

export default MobileViewEvent
